/* ========================================================================
   © 2026 FixMySiteKit Framework. All rights reserved.
   useSeoHead — lightweight document.head manipulation for SPA SEO.
   Injects title, meta description, canonical, OpenGraph, Twitter, and
   JSON-LD schema without needing a heavy helmet library.
   ======================================================================== */
import { useEffect } from "react";

const MANAGED_ATTR = "data-fmsk-seo";

function upsertMeta({ name, property, content }) {
    if (!content) return;
    const selector = name
        ? `meta[name="${name}"]`
        : `meta[property="${property}"]`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        if (name) el.setAttribute("name", name);
        if (property) el.setAttribute("property", property);
        el.setAttribute(MANAGED_ATTR, "1");
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"][${MANAGED_ATTR}]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute(MANAGED_ATTR, "1");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
    if (!data) return;
    let el = document.head.querySelector(`script[type="application/ld+json"][data-id="${id}"]`);
    if (!el) {
        el = document.createElement("script");
        el.setAttribute("type", "application/ld+json");
        el.setAttribute("data-id", id);
        el.setAttribute(MANAGED_ATTR, "1");
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

function cleanupManaged() {
    document.head
        .querySelectorAll(`[${MANAGED_ATTR}]`)
        .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
}

export default function useSeoHead({
    title,
    description,
    canonical,
    ogImage,
    keywords,
    robots = "index, follow",
    jsonLd, // object or array of objects — each gets a stable id
}) {
    useEffect(() => {
        const prevTitle = document.title;
        if (title) document.title = title;

        upsertMeta({ name: "description", content: description });
        upsertMeta({ name: "keywords", content: keywords });
        upsertMeta({ name: "robots", content: robots });
        upsertLink("canonical", canonical);

        // OpenGraph
        upsertMeta({ property: "og:type", content: "website" });
        upsertMeta({ property: "og:title", content: title });
        upsertMeta({ property: "og:description", content: description });
        upsertMeta({ property: "og:url", content: canonical });
        if (ogImage) upsertMeta({ property: "og:image", content: ogImage });

        // Twitter
        upsertMeta({ name: "twitter:card", content: "summary_large_image" });
        upsertMeta({ name: "twitter:title", content: title });
        upsertMeta({ name: "twitter:description", content: description });
        if (ogImage) upsertMeta({ name: "twitter:image", content: ogImage });

        if (jsonLd) {
            const list = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            list.forEach((data, idx) => upsertJsonLd(`fmsk-${idx}`, data));
        }

        return () => {
            document.title = prevTitle;
            cleanupManaged();
        };
    }, [title, description, canonical, ogImage, keywords, robots, jsonLd]);
}
